import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Check,
  RestartAlt,
} from '@mui/icons-material';
import { companiesService, projectService } from '../services/api';
import { Company, AppUser, AdhocProject, AdhocCompany } from '../types';
import { useAuth } from '../contexts/AuthContext';
import NewCompanyModal, { CompanyFormData, OtherCompanyUser } from '../components/NewCompanyModal';

const getProjectNameMap = (data: unknown): Record<string, string> => {
  const map: Record<string, string> = {};
  let projects: unknown[] = [];

  if (Array.isArray(data)) {
    projects = data;
  } else if (data && typeof data === 'object') {
    const nested = data as { projects?: unknown[]; data?: unknown[] };
    if (Array.isArray(nested.projects)) {
      projects = nested.projects;
    } else if (Array.isArray(nested.data)) {
      projects = nested.data;
    }
  }

  projects.forEach((project) => {
    if (!project || typeof project !== 'object') return;
    const projectRecord = project as Record<string, unknown>;
    const id = projectRecord.project_id || projectRecord.id;
    const name = projectRecord.project_name || projectRecord.name;
    if (typeof id === 'string' && typeof name === 'string' && name.trim()) {
      map[id] = name;
    }
  });

  return map;
};

// Annet / adhoc rows are the per-project catch-all companies. The API marks
// them with project_id; older rows are identified by the "Annet …" name.
const isAnnetCompany = (company: Company) =>
  Boolean(company.project_id) || /^annet(\s|$)/i.test(company.company_name.trim());

const PriceEditor: React.FC<{
  price: number;
  label: string;
  onSave: (price: number) => Promise<void>;
  /** Shown when set; resets the price to its default (e.g. the Annet price) */
  onReset?: () => Promise<void>;
}> = ({ price: currentPrice, label, onSave, onReset }) => {
  const [value, setValue] = useState(() => String(currentPrice));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(String(currentPrice));
    setError(null);
  }, [currentPrice]);

  const parsed = parseFloat(value.trim().replace(',', '.'));
  const unchanged = Number.isFinite(parsed) && parsed === Number(currentPrice);

  const run = async (action: () => Promise<void>, failure: string) => {
    try {
      setSaving(true);
      setError(null);
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : failure);
    } finally {
      setSaving(false);
    }
  };

  const commit = async () => {
    if (saving) return;

    const normalized = value.trim().replace(',', '.');
    const price = Number(normalized);
    if (!/^\d+(\.\d+)?$/.test(normalized) || !Number.isFinite(price) || price < 0) {
      setError('Enter a price of 0 or more');
      return;
    }
    if (price === Number(currentPrice)) {
      setError(null);
      return;
    }

    await run(() => onSave(price), 'Failed to update price');
  };

  return (
    <Box onClick={(event) => event.stopPropagation()}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <TextField
          size="small"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void commit();
            }
          }}
          disabled={saving}
          inputProps={{
            'aria-label': `Charging price for ${label}`,
            inputMode: 'decimal',
          }}
          sx={{
            width: 88,
            '& .MuiOutlinedInput-root': {
              height: 32,
              fontFamily: 'Montserrat',
              fontSize: '14px',
            },
            '& .MuiInputBase-input': {
              padding: '4px 8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
            },
          }}
        />
        <Typography sx={{
          fontFamily: 'Montserrat',
          fontSize: '13px',
          color: 'rgba(0,0,0,0.6)',
          whiteSpace: 'nowrap',
        }}>
          kr/kWh
        </Typography>
        <IconButton
          size="small"
          aria-label={`Save price for ${label}`}
          onClick={() => { void commit(); }}
          disabled={saving || unchanged || value.trim() === ''}
          sx={{ color: '#337A5C' }}
        >
          {saving ? <CircularProgress size={16} /> : <Check fontSize="small" />}
        </IconButton>
        {onReset && (
          <Tooltip title="Use the Annet price">
            <span>
              <IconButton
                size="small"
                aria-label={`Reset price for ${label}`}
                onClick={() => { void run(onReset, 'Failed to reset price'); }}
                disabled={saving}
              >
                <RestartAlt fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {error && (
        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', color: '#d32f2f', mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

const PROJECT_CHIP_COLORS = [
  { color: '#337A5C', backgroundColor: 'rgba(51, 122, 92, 0.12)', borderColor: 'rgba(51, 122, 92, 0.28)' },
  { color: '#1976D2', backgroundColor: 'rgba(25, 118, 210, 0.12)', borderColor: 'rgba(25, 118, 210, 0.28)' },
  { color: '#ED6C02', backgroundColor: 'rgba(237, 108, 2, 0.12)', borderColor: 'rgba(237, 108, 2, 0.28)' },
  { color: '#7B1FA2', backgroundColor: 'rgba(123, 31, 162, 0.12)', borderColor: 'rgba(123, 31, 162, 0.28)' },
  { color: '#00838F', backgroundColor: 'rgba(0, 131, 143, 0.12)', borderColor: 'rgba(0, 131, 143, 0.28)' },
  { color: '#C2185B', backgroundColor: 'rgba(194, 24, 91, 0.12)', borderColor: 'rgba(194, 24, 91, 0.28)' },
  { color: '#3949AB', backgroundColor: 'rgba(57, 73, 171, 0.12)', borderColor: 'rgba(57, 73, 171, 0.28)' },
  { color: '#6D4C41', backgroundColor: 'rgba(109, 76, 65, 0.12)', borderColor: 'rgba(109, 76, 65, 0.28)' },
  { color: '#E64A19', backgroundColor: 'rgba(230, 74, 25, 0.12)', borderColor: 'rgba(230, 74, 25, 0.28)' },
  { color: '#2E7D32', backgroundColor: 'rgba(46, 125, 50, 0.12)', borderColor: 'rgba(46, 125, 50, 0.28)' },
];


const Companies: React.FC = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [projectNamesById, setProjectNamesById] = useState<Record<string, string>>({});
  const [annetExpanded, setAnnetExpanded] = useState(false);
  const [expandedAnnetProjects, setExpandedAnnetProjects] = useState<Set<string>>(() => new Set());
  const [adhocByProject, setAdhocByProject] = useState<Record<string, AdhocProject>>({});
  const [adhocLoading, setAdhocLoading] = useState(false);
  const [adhocError, setAdhocError] = useState<string | null>(null);
  const [isMoveLoading, setIsMoveLoading] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [moveWarning, setMoveWarning] = useState<string | null>(null);

  const fetchCompanies = useCallback(async (showLoading = true) => {
    if (!user?.orgId) {
      setError('No organization ID available');
      setLoading(false);
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const [companiesResponse, projectsResponse] = await Promise.all([
        companiesService.getCompanies(user.orgId),
        projectService.getProjects(user.orgId).catch((err) => {
          console.error('Error fetching projects:', err);
          return null;
        }),
      ]);

      if (companiesResponse.success) {
        setCompanies(companiesResponse.data.companies);
      } else {
        setError('Failed to fetch companies');
      }

      if (projectsResponse?.success) {
        setProjectNamesById(getProjectNameMap(projectsResponse.data));
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch companies');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [user?.orgId]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Loaded separately: the backend scans a year of charging sessions per project.
  const fetchAdhocCompanies = useCallback(async () => {
    if (!user?.orgId) return;
    try {
      setAdhocLoading(true);
      setAdhocError(null);
      const response = await companiesService.getAdhocCompanies(user.orgId);
      const byProject: Record<string, AdhocProject> = {};
      (response.data?.projects ?? []).forEach((project) => {
        byProject[project.project_id] = project;
      });
      setAdhocByProject(byProject);
    } catch (err) {
      console.error('Error fetching adhoc companies:', err);
      setAdhocError(err instanceof Error ? err.message : 'Failed to load adhoc companies');
    } finally {
      setAdhocLoading(false);
    }
  }, [user?.orgId]);

  useEffect(() => {
    fetchAdhocCompanies();
  }, [fetchAdhocCompanies]);

  const toggleAnnetProject = (projectId: string) => {
    setExpandedAnnetProjects((current) => {
      const next = new Set(current);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const toIdentifierList = (value?: string[] | string | null) => {
    if (!value) return [];
    return Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
  };

  const getCompanyIdentifiers = (company: Company) => ({
    rfid: toIdentifierList(company.identifiers?.rfid || company.rfid),
    evcc_id: toIdentifierList(company.identifiers?.evcc_id || company.evcc_id),
    pin_code: (company.project_pins ?? [])
      .map((projectPin) => projectPin.pin)
      .filter(Boolean),
  });

  const getProjectName = (projectId: string) => projectNamesById[projectId];

  const getAnnetDisplayName = useCallback((company: Company) => {
    if (company.project_id) {
      const projectName = projectNamesById[company.project_id];
      if (projectName) return projectName;
    }

    return company.company_name.replace(/^annet\s+/i, '').trim() || company.company_name;
  }, [projectNamesById]);

  const projectChipColorsById = useMemo(() => {
    const projectIds = Array.from(new Set(
      companies.flatMap((company) =>
        (company.project_pins ?? []).map((projectPin) => projectPin.project_id)
      )
    )).sort();

    return projectIds.reduce<Record<string, typeof PROJECT_CHIP_COLORS[number]>>((map, projectId, index) => {
      map[projectId] = PROJECT_CHIP_COLORS[index % PROJECT_CHIP_COLORS.length];
      return map;
    }, {});
  }, [companies]);

  // Named app users (people with a user_name in the whitelist) from all
  // companies other than the one being edited. Annet / adhoc companies are
  // included because moving a guest to a permanent company is the main use-case.
  // Plain RFID identifiers without a name are excluded.
  const otherCompanyUsers = useMemo((): OtherCompanyUser[] => {
    if (!editingCompany) return [];
    const editingId = String(editingCompany.id);
    const users: OtherCompanyUser[] = [];
    companies.forEach((company) => {
      if (String(company.id) === editingId) return;
      // app_users already contains only whitelist entries with a user_name —
      // plain RFID cards without a name never appear here.
      (company.app_users ?? []).forEach((u) => {
        if (u.rfid && u.name) {
          // For Annet rows, use the project name as a friendlier label.
          let companyLabel = company.company_name;
          if (isAnnetCompany(company)) {
            if (company.project_id) {
              companyLabel = projectNamesById[company.project_id] || company.company_name;
            }
            companyLabel = companyLabel.replace(/^annet\s+/i, '').trim() || companyLabel;
          }
          users.push({ rfid: u.rfid, name: u.name, companyName: companyLabel });
        }
      });
    });
    return users;
  }, [companies, editingCompany, projectNamesById]);

  const companyMatchesSearch = (company: Company) => {
    const identifiers = getCompanyIdentifiers(company);
    const search = searchTerm.toLowerCase();

    return (
      company.company_name.toLowerCase().includes(search) ||
      company.charging_price.toString().includes(searchTerm) ||
      identifiers.pin_code.some(pin => pin.toLowerCase().includes(search)) ||
      (company.project_pins ?? []).some((projectPin) =>
        getProjectName(projectPin.project_id)?.toLowerCase().includes(search)
      ) ||
      (isAnnetCompany(company) && getAnnetDisplayName(company).toLowerCase().includes(search))
    );
  };

  const getAdhocCompanies = (annet: Company): AdhocCompany[] =>
    (annet.project_id && adhocByProject[annet.project_id]?.companies) || [];

  // All adhoc companies when the project itself matches the search, otherwise
  // only the ones whose name matches.
  const getVisibleAdhocCompanies = (annet: Company): AdhocCompany[] => {
    const adhoc = getAdhocCompanies(annet);
    const search = searchTerm.trim().toLowerCase();
    if (!search || companyMatchesSearch(annet)) return adhoc;
    return adhoc.filter((company) => company.company_name.toLowerCase().includes(search));
  };

  const filteredCompanies = companies.filter((company) =>
    companyMatchesSearch(company) ||
    (isAnnetCompany(company) && getVisibleAdhocCompanies(company).length > 0)
  );

  // Sort the filtered companies
  const sortedCompanies = [...filteredCompanies].sort((a, b) => {
    if (!sortField) return 0;
    
    let aValue: any;
    let bValue: any;
    
    switch (sortField) {
      case 'name':
        aValue = a.company_name.toLowerCase();
        bValue = b.company_name.toLowerCase();
        break;
      case 'charging_price':
        aValue = a.charging_price;
        bValue = b.charging_price;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  type TableItem =
    | { type: 'company'; company: Company }
    | { type: 'annet-group'; companies: Company[] };

  const tableItems = useMemo(() => {
    const annetCompanies = sortedCompanies
      .filter(isAnnetCompany)
      .sort((a, b) => {
        if (sortField === 'charging_price') {
          return sortDirection === 'asc'
            ? a.charging_price - b.charging_price
            : b.charging_price - a.charging_price;
        }
        const byName = getAnnetDisplayName(a).localeCompare(getAnnetDisplayName(b), 'nb', { sensitivity: 'base' });
        return sortField === 'name' && sortDirection === 'desc' ? -byName : byName;
      });
    const regularCompanies = sortedCompanies.filter((company) => !isAnnetCompany(company));
    const items: TableItem[] = regularCompanies.map((company) => ({ type: 'company', company }));

    if (annetCompanies.length > 0) {
      items.push({ type: 'annet-group', companies: annetCompanies });
    }

    return [...items].sort((a, b) => {
      if (!sortField) {
        if (a.type === 'annet-group') return 1;
        if (b.type === 'annet-group') return -1;
        return 0;
      }

      const getSortValue = (item: TableItem) => {
        if (item.type === 'annet-group') {
          return sortField === 'charging_price'
            ? item.companies[0]?.charging_price ?? 0
            : 'annet';
        }
        return sortField === 'charging_price'
          ? item.company.charging_price
          : item.company.company_name.toLowerCase();
      };

      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortedCompanies, sortField, sortDirection, getAnnetDisplayName]);

  useEffect(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return;
    setAnnetExpanded(true);

    const matchingProjects = Object.values(adhocByProject)
      .filter((project) => project.companies.some((company) =>
        company.company_name.toLowerCase().includes(search)
      ))
      .map((project) => project.project_id);
    if (matchingProjects.length > 0) {
      setExpandedAnnetProjects((current) => new Set([...Array.from(current), ...matchingProjects]));
    }
  }, [searchTerm, adhocByProject]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField === field) {
      // Show the appropriate arrow for the current sort direction
      return (
        <Box
          component="img"
          src={sortDirection === 'asc' ? '/ChevronDownIcon.svg' : '/ChevronUpIcon.svg'}
          alt={sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'}
          sx={{ width: 18, height: 18, ml: 1, verticalAlign: 'middle' }}
        />
      );
    }
    
    // Show a default chevron for hover state
    return (
      <Box
        component="img"
        src={process.env.PUBLIC_URL + "/ChevronDownIcon.svg"}
        alt="Sort"
        sx={{ width: 18, height: 18, ml: 1, opacity: 0.5, verticalAlign: 'middle' }}
      />
    );
  };

  const renderProjectPins = (company: Company) => {
    const projectPins = company.project_pins?.filter((projectPin) => projectPin.pin) || [];

    if (projectPins.length === 0) {
      return (
        <Typography sx={{ 
          fontFamily: 'Montserrat',
          fontSize: '14px',
          color: 'rgba(0,0,0,0.6)',
          lineHeight: '1.43',
          fontWeight: 500,
        }}>
          N/A
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
        {projectPins.map((projectPin) => {
          const projectName = getProjectName(projectPin.project_id);
          const chipColor = projectChipColorsById[projectPin.project_id] || PROJECT_CHIP_COLORS[0];
          return (
            <Chip
              key={`${projectPin.project_id}-${projectPin.pin}`}
              label={projectName ? `${projectName}: ${projectPin.pin}` : projectPin.pin}
              size="small"
              sx={{
                backgroundColor: chipColor.backgroundColor,
                color: chipColor.color,
                border: `1px solid ${chipColor.borderColor}`,
                fontFamily: 'Montserrat',
                fontSize: '11px',
                fontWeight: 500,
              }}
            />
          );
        })}
      </Box>
    );
  };

  const renderCompanyIdentifiers = (company: Company) => {
    const { rfid: rfidValues, evcc_id: evccIdValues } = getCompanyIdentifiers(company);
    const appUsers: AppUser[] = company.app_users ?? [];

    if (rfidValues.length === 0 && evccIdValues.length === 0 && appUsers.length === 0) {
      return (
        <Typography sx={{ 
          fontFamily: 'Montserrat',
          fontSize: '14px',
          color: 'rgba(0,0,0,0.6)',
          lineHeight: '1.43',
          fontWeight: 500,
        }}>
          N/A
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
        {(rfidValues.length > 0 || evccIdValues.length > 0) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
            {rfidValues.map((rfid) => (
              <Chip
                key={`rfid-${rfid}`}
                label={`RFID: ${rfid}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(51, 122, 92, 0.12)',
                  color: '#337A5C',
                  border: '1px solid rgba(51, 122, 92, 0.28)',
                  fontFamily: 'Montserrat',
                  fontSize: '11px',
                  fontWeight: 500,
                }}
              />
            ))}
            {evccIdValues.map((evccId) => (
              <Chip
                key={`evcc-${evccId}`}
                label={`EVCC ID: ${evccId}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  color: '#1976D2',
                  border: '1px solid rgba(25, 118, 210, 0.28)',
                  fontFamily: 'Montserrat',
                  fontSize: '11px',
                  fontWeight: 500,
                }}
              />
            ))}
          </Box>
        )}
        {appUsers.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', width: '100%' }}>
            <Typography sx={{
              fontFamily: 'Montserrat',
              fontSize: '10px',
              fontWeight: 600,
              color: 'rgba(0,0,0,0.45)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}>
              App Users
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
              {appUsers.map((u) => (
                <Chip
                  key={`app-${u.rfid}`}
                  label={u.name}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(123, 31, 162, 0.10)',
                    color: '#7B1FA2',
                    border: '1px solid rgba(123, 31, 162, 0.28)',
                    fontFamily: 'Montserrat',
                    fontSize: '11px',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const adhocCellSx = { padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' };
  const adhocMutedTextSx = {
    fontFamily: 'Montserrat',
    fontSize: '13px',
    color: 'rgba(0,0,0,0.6)',
    lineHeight: '1.43',
  };

  const renderAdhocMessageRow = (key: string, content: React.ReactNode) => (
    <TableRow key={key} sx={{ backgroundColor: '#FFFFFF' }}>
      <TableCell colSpan={5} sx={{ ...adhocCellSx, paddingLeft: '88px' }}>
        {content}
      </TableCell>
    </TableRow>
  );

  const renderAdhocRows = (projectId: string, adhocCompanies: AdhocCompany[]) => {
    if (adhocLoading && !adhocByProject[projectId]) {
      return renderAdhocMessageRow(`adhoc-loading-${projectId}`, (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography sx={adhocMutedTextSx}>Loading adhoc companies…</Typography>
        </Box>
      ));
    }

    if (adhocError && !adhocByProject[projectId]) {
      return renderAdhocMessageRow(`adhoc-error-${projectId}`, (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ ...adhocMutedTextSx, color: '#d32f2f' }}>
            Could not load adhoc companies: {adhocError}
          </Typography>
          <Button size="small" onClick={(event) => { event.stopPropagation(); fetchAdhocCompanies(); }}>
            Retry
          </Button>
        </Box>
      ));
    }

    if (adhocCompanies.length === 0) {
      return renderAdhocMessageRow(`adhoc-empty-${projectId}`, (
        <Typography sx={adhocMutedTextSx}>
          No adhoc companies on this project in the last 12 months.
        </Typography>
      ));
    }

    return adhocCompanies.map((adhoc) => (
      <TableRow
        key={`adhoc-${projectId}-${adhoc.company_key}`}
        hover
        sx={{ backgroundColor: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' } }}
      >
        <TableCell sx={{ ...adhocCellSx, paddingLeft: '88px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{
              fontFamily: 'Montserrat',
              fontSize: '14px',
              color: 'rgba(0,0,0,0.87)',
              lineHeight: '1.43',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}>
              {adhoc.company_name}
            </Typography>
            {adhoc.has_custom_price && (
              <Chip
                label="Custom price"
                size="small"
                sx={{
                  height: '20px',
                  fontFamily: 'Montserrat',
                  fontSize: '11px',
                  fontWeight: 500,
                  backgroundColor: 'rgba(51, 122, 92, 0.12)',
                  color: '#337A5C',
                }}
              />
            )}
          </Box>
          <Typography sx={{ ...adhocMutedTextSx, fontSize: '11px', mt: 0.25 }}>
            {adhoc.session_count} {adhoc.session_count === 1 ? 'session' : 'sessions'}
            {adhoc.last_session_date ? ` · last ${adhoc.last_session_date}` : ''}
          </Typography>
        </TableCell>
        <TableCell sx={adhocCellSx}>
          <PriceEditor
            price={adhoc.charging_price}
            label={adhoc.company_name}
            onSave={(price) => handleSaveAdhocPrice(projectId, adhoc, price)}
            onReset={adhoc.has_custom_price ? () => handleSaveAdhocPrice(projectId, adhoc, null) : undefined}
          />
        </TableCell>
        <TableCell sx={{ ...adhocCellSx, textAlign: 'center' }}>
          <Typography sx={adhocMutedTextSx}>
            {adhoc.user_count > 0
              ? `${adhoc.user_count} guest ${adhoc.user_count === 1 ? 'user' : 'users'}`
              : 'No current users'}
          </Typography>
        </TableCell>
        <TableCell sx={{ ...adhocCellSx, textAlign: 'center' }}>
          <Typography sx={adhocMutedTextSx}>N/A</Typography>
        </TableCell>
        <TableCell sx={adhocCellSx} />
      </TableRow>
    ));
  };

  const handleOpenModal = () => {
    setEditingCompany(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCompany(null);
    setMoveError(null);
    setMoveWarning(null);
  };

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLButtonElement>, company: Company) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedCompany(company);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedCompany(null);
  };

  const handleEditSelectedCompany = () => {
    if (!selectedCompany) return;
    setEditingCompany(selectedCompany);
    setIsModalOpen(true);
    handleCloseActionMenu();
  };

  const handleRequestDeleteSelectedCompany = () => {
    if (!selectedCompany) return;
    setCompanyToDelete(selectedCompany);
    handleCloseActionMenu();
  };

  const handleCloseDeleteDialog = () => {
    if (isDeleting) return;
    setCompanyToDelete(null);
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;

    try {
      setIsDeleting(true);
      setError(null);
      await companiesService.deleteCompany(companyToDelete.id);
      await fetchCompanies(false);
      setCompanyToDelete(null);
    } catch (err) {
      console.error('Error deleting company:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete company');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMoveUser = async (rfid: string) => {
    if (!editingCompany || !user?.orgId) return;
    setIsMoveLoading(true);
    setMoveError(null);
    setMoveWarning(null);
    try {
      const response = await companiesService.moveUser(user.orgId, {
        rfid,
        to_company_id: Number(editingCompany.id),
      });
      if (!response.success) {
        setMoveError((response as any).message || 'Failed to move user');
        return;
      }

      const data = response.data as any;

      // The API can return 200 but the Supabase write may not have landed.
      // Check the diagnostics field so we surface silent failures.
      if (data?.diagnostics?.move_persisted_in_db === false) {
        setMoveError(
          'The user was not moved — the database did not confirm the change. ' +
          'Please try again or contact support.'
        );
        return;
      }

      // Non-fatal: Firebase update was skipped (e.g. device not in Firebase yet).
      // The 60-second sync will catch up, but warn the admin.
      if (data?.firebase_warning) {
        setMoveWarning(`Firebase update pending: ${data.firebase_warning}`);
      }

      // Refresh the companies list and update the editing company so the
      // new app user appears immediately without closing the dialog.
      const freshResponse = await companiesService.getCompanies(user.orgId);
      if (freshResponse.success) {
        const freshList = freshResponse.data.companies;
        setCompanies(freshList);
        const refreshed = freshList.find((c) => String(c.id) === String(editingCompany.id));
        if (refreshed) setEditingCompany(refreshed);
      }
    } catch (err) {
      setMoveError(err instanceof Error ? err.message : 'Failed to move user');
    } finally {
      setIsMoveLoading(false);
    }
  };

  const getCompanyInitialData = (company: Company): CompanyFormData => {
    const identifiers = getCompanyIdentifiers(company);
    // Include app users' RFIDs so they are not wiped when the form is saved.
    const appUserRfids = (company.app_users ?? []).map((u) => u.rfid).filter(Boolean);
    const allRfids = Array.from(new Set([...identifiers.rfid, ...appUserRfids]));

    return {
      company_name: company.company_name,
      charging_price: company.charging_price,
      rfid: allRfids,
      evcc_id: identifiers.evcc_id,
      pin_code: identifiers.pin_code,
    };
  };

  const handleSaveAnnetPrice = async (company: Company, price: number) => {
    if (!user?.orgId) {
      throw new Error('No organization ID available');
    }

    await companiesService.updateCompanyPrice(company.id, user.orgId, price);
    setCompanies((current) =>
      current.map((item) =>
        String(item.id) === String(company.id) ? { ...item, charging_price: price } : item
      )
    );

    const projectId = company.project_id;
    if (projectId) {
      setAdhocByProject((current) => {
        const project = current[projectId];
        if (!project) return current;
        return {
          ...current,
          [projectId]: {
            ...project,
            annet_price: price,
            companies: project.companies.map((adhoc) =>
              adhoc.has_custom_price ? adhoc : { ...adhoc, charging_price: price }
            ),
          },
        };
      });
    }
  };

  const handleSaveAdhocPrice = async (projectId: string, adhoc: AdhocCompany, price: number | null) => {
    if (!user?.orgId) {
      throw new Error('No organization ID available');
    }

    await companiesService.setAdhocCompanyPrice(user.orgId, {
      project_id: projectId,
      company_name: adhoc.company_name,
      charging_price: price,
    });

    setAdhocByProject((current) => {
      const project = current[projectId];
      if (!project) return current;
      return {
        ...current,
        [projectId]: {
          ...project,
          companies: project.companies.map((item) =>
            item.company_key === adhoc.company_key
              ? {
                  ...item,
                  charging_price: price ?? project.annet_price,
                  has_custom_price: price !== null,
                }
              : item
          ),
        },
      };
    });
  };

  const handleSubmitCompany = async (companyData: CompanyFormData) => {
    try {
      setIsCreating(true);
      setError(null);
      
      const requestData = {
        company_name: companyData.company_name,
        charging_price: companyData.charging_price,
        rfid: companyData.rfid,
        evcc_id: companyData.evcc_id,
        ...(editingCompany ? {} : { pin_code: companyData.pin_code }),
      };
      
      console.log('Creating company with data:', requestData);
      console.log('Data type check:', {
        company_name: typeof requestData.company_name,
        charging_price: typeof requestData.charging_price,
        charging_price_value: requestData.charging_price,
        rfid: Array.isArray(requestData.rfid) ? 'array' : typeof requestData.rfid,
        evcc_id: Array.isArray(requestData.evcc_id) ? 'array' : typeof requestData.evcc_id,
        pin_code: Array.isArray(requestData.pin_code) ? 'array' : typeof requestData.pin_code,
      });
      
      const response = editingCompany
        ? await companiesService.updateCompany(editingCompany.id, {
            ...requestData,
            org_id: user!.orgId!,
          })
        : await companiesService.createCompany(user!.orgId!, requestData);
      
      if (response.success) {
        await fetchCompanies(false);
        setIsModalOpen(false);
        setEditingCompany(null);
      } else {
        setError(editingCompany ? 'Failed to update company' : 'Failed to create company');
      }
    } catch (err) {
      console.error(editingCompany ? 'Error updating company:' : 'Error creating company:', err);
      setError(err instanceof Error ? err.message : editingCompany ? 'Failed to update company' : 'Failed to create company');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Box sx={{ 
      px: 3, py: 0, // 24px horizontal padding, no vertical padding
      backgroundColor: '#FAFAFA', 
      minHeight: '100vh',
      color: 'black'
    }}>
      {/* Header with Add Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Typography 
          variant="h3" 
          sx={{ 
            color: 'rgba(0, 0, 0, 0.87)',
            fontFamily: 'Montserrat',
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '28px',
          }}
        >
          Companies
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenModal}
          sx={{
            backgroundColor: (theme) => theme.palette.primary.light,
            color: 'white',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.main,
              opacity: 0.9,
            },
          }}
        >
          ADD COMPANY
        </Button>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '300px',
          textAlign: 'center'
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: 'rgba(0, 0, 0, 0.6)' }}>
            Loading companies...
          </Typography>
        </Box>
      ) : (
        <>
      {/* Search and Filter Section */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        mb: 4 
      }}>
        <TextField
          placeholder="Search companies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search sx={{ color: 'rgba(0, 0, 0, 0.6)' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: '220px',
            '& .MuiOutlinedInput-root': {
              height: '40px',
              padding: '0 12px',
              borderRadius: '4px',
              border: '1px solid rgba(0,0,0,0.23)',
              '&:hover': {
                border: '1px solid rgba(0,0,0,0.87)',
              },
              '&.Mui-focused': {
                border: '2px solid #337a5c',
              },
            },
            '& .MuiInputBase-input': {
              height: '40px',
              padding: '8px 0',
              color: 'rgba(0, 0, 0, 0.60)',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(0, 0, 0, 0.60)',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        />
      </Box>

      {/* Separator Line */}
      <Box sx={{ 
        height: '1px', 
        backgroundColor: '#ddd', 
        mb: 2
      }} />

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 'none', overflowX: 'auto' }}>
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#FAFAFA' }}>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  minWidth: 300,
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('name')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Name
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('name')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  minWidth: 220,
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('charging_price')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Charging Price
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('charging_price')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  minWidth: 120,
                  textAlign: 'center'
                }}
              >
                Users
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  minWidth: 220,
                  textAlign: 'center'
                }}
              >
                Access code
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 500, 
                borderBottom: '1px solid #616161',
                padding: '16px',
                fontFamily: 'Roboto',
                fontSize: '14px',
                color: 'rgba(0,0,0,0.87)',
                letterSpacing: '0.17px',
                lineHeight: '24px',
                textAlign: 'right'
              }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableItems
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((item) => {
                if (item.type === 'annet-group') {
                  const sharedPrice = Array.from(new Set(item.companies.map((company) => company.charging_price)));
                  return (
                    <React.Fragment key="annet-group">
                      <TableRow
                        hover
                        onClick={() => setAnnetExpanded((expanded) => !expanded)}
                        aria-expanded={annetExpanded}
                        sx={{
                          backgroundColor: '#FAFAFA',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                        }}
                      >
                        <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {annetExpanded ? (
                              <KeyboardArrowDown sx={{ color: 'rgba(0,0,0,0.54)' }} />
                            ) : (
                              <KeyboardArrowRight sx={{ color: 'rgba(0,0,0,0.54)' }} />
                            )}
                            <Typography sx={{ 
                              fontFamily: 'Montserrat',
                              fontSize: '14px',
                              color: 'rgba(0,0,0,0.87)',
                              lineHeight: '1.43',
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                            }}>
                              Annet
                            </Typography>
                            <Chip
                              label={`${item.companies.length} ${item.companies.length === 1 ? 'project' : 'projects'}`}
                              size="small"
                              sx={{
                                height: '20px',
                                fontFamily: 'Montserrat',
                                fontSize: '11px',
                                fontWeight: 500,
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                          <Typography sx={{ 
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            color: 'rgba(0,0,0,0.87)',
                            lineHeight: '1.43',
                            fontWeight: 500,
                          }}>
                            {sharedPrice.length === 1 ? `${sharedPrice[0]} kr/kWh` : 'Per company'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)', textAlign: 'center' }}>
                          <Typography sx={{ 
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            color: 'rgba(0,0,0,0.6)',
                            lineHeight: '1.43',
                            fontWeight: 500,
                          }}>
                            N/A
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)', textAlign: 'center' }}>
                          <Typography sx={{ 
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            color: 'rgba(0,0,0,0.6)',
                            lineHeight: '1.43',
                            fontWeight: 500,
                          }}>
                            N/A
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ 
                          padding: '12px 16px', 
                          borderBottom: '1px solid rgba(0,0,0,0.12)',
                          textAlign: 'right'
                        }} />
                      </TableRow>
                      {annetExpanded && item.companies.map((company) => {
                        const projectId = company.project_id ? String(company.project_id) : null;
                        const projectExpanded = projectId ? expandedAnnetProjects.has(projectId) : false;
                        const adhocCompanies = getVisibleAdhocCompanies(company);
                        const customCount = adhocCompanies.filter((adhoc) => adhoc.has_custom_price).length;

                        return (
                          <React.Fragment key={company.id}>
                            <TableRow
                              hover
                              onClick={projectId ? () => toggleAnnetProject(projectId) : undefined}
                              aria-expanded={projectId ? projectExpanded : undefined}
                              sx={{
                                backgroundColor: '#F3F3F3',
                                cursor: projectId ? 'pointer' : 'default',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                              }}
                            >
                              <TableCell sx={{ padding: '12px 16px 12px 40px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {projectId && (projectExpanded ? (
                                    <KeyboardArrowDown sx={{ color: 'rgba(0,0,0,0.54)' }} />
                                  ) : (
                                    <KeyboardArrowRight sx={{ color: 'rgba(0,0,0,0.54)' }} />
                                  ))}
                                  <Typography sx={{ 
                                    fontFamily: 'Montserrat',
                                    fontSize: '14px',
                                    color: 'rgba(0,0,0,0.87)',
                                    lineHeight: '1.43',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {getAnnetDisplayName(company)}
                                  </Typography>
                                  {projectId && adhocByProject[projectId] && (
                                    <Chip
                                      label={`${adhocCompanies.length} ${adhocCompanies.length === 1 ? 'company' : 'companies'}`}
                                      size="small"
                                      sx={{ height: '20px', fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 500 }}
                                    />
                                  )}
                                  {customCount > 0 && (
                                    <Chip
                                      label={`${customCount} custom`}
                                      size="small"
                                      sx={{
                                        height: '20px',
                                        fontFamily: 'Montserrat',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        backgroundColor: 'rgba(51, 122, 92, 0.12)',
                                        color: '#337A5C',
                                      }}
                                    />
                                  )}
                                </Box>
                                <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', color: 'rgba(0,0,0,0.5)', mt: 0.25, ml: projectId ? 4 : 0 }}>
                                  Annet price (default for adhoc companies)
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                                <PriceEditor
                                  price={company.charging_price}
                                  label={company.company_name}
                                  onSave={(price) => handleSaveAnnetPrice(company, price)}
                                />
                              </TableCell>
                              <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)', textAlign: 'center' }}>
                                {renderCompanyIdentifiers(company)}
                              </TableCell>
                              <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)', textAlign: 'center' }}>
                                {renderProjectPins(company)}
                              </TableCell>
                              <TableCell sx={{ 
                                padding: '12px 16px', 
                                borderBottom: '1px solid rgba(0,0,0,0.12)',
                                textAlign: 'right'
                              }}>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenActionMenu(event, company);
                                  }}
                                >
                                  <MoreVert />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            {projectId && projectExpanded && renderAdhocRows(projectId, adhocCompanies)}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                }

                const { company } = item;
                return (
                  <TableRow key={company.id} hover sx={{ backgroundColor: '#FAFAFA', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                    <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                      <Typography sx={{ 
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        color: 'rgba(0,0,0,0.87)',
                        lineHeight: '1.43',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}>
                        {company.company_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                      <Typography sx={{ 
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        color: 'rgba(0,0,0,0.87)',
                        lineHeight: '1.43',
                        fontWeight: 500,
                      }}>
                        {company.charging_price} kr/kWh
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)', textAlign: 'center' }}>
                      {renderCompanyIdentifiers(company)}
                    </TableCell>
                    <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)', textAlign: 'center' }}>
                      {renderProjectPins(company)}
                    </TableCell>
                    <TableCell sx={{ 
                      padding: '12px 16px', 
                      borderBottom: '1px solid rgba(0,0,0,0.12)',
                      textAlign: 'right'
                    }}>
                      <IconButton size="small" onClick={(event) => handleOpenActionMenu(event, company)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEditSelectedCompany}>Edit</MenuItem>
        <MenuItem onClick={handleRequestDeleteSelectedCompany} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={tableItems.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ 
          borderTop: '1px solid rgba(0,0,0,0.12)',
          '& .MuiTablePagination-toolbar': {
            paddingLeft: 0,
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-selectLabel': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-displayedRows': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-select': {
            fontFamily: 'Roboto',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.3px',
            lineHeight: '19px'
          }
        }}
      />

      {/* New Company Modal */}
      <NewCompanyModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitCompany}
        initialData={editingCompany ? getCompanyInitialData(editingCompany) : undefined}
        isLoading={isCreating}
        mode={editingCompany ? 'edit' : 'create'}
        appUsers={editingCompany?.app_users ?? []}
        otherCompanyUsers={editingCompany ? otherCompanyUsers : []}
        onMoveUser={editingCompany ? handleMoveUser : undefined}
        isMoveLoading={isMoveLoading}
        moveError={moveError}
        moveWarning={moveWarning}
      />
        </>
      )}

      <Dialog open={Boolean(companyToDelete)} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Company</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {companyToDelete?.company_name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCompany}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Companies;
